import { Area, AreaChart, CartesianGrid, ResponsiveContainer } from 'recharts';
import { useGoldPrice } from '@/hooks/useGoldPrice';
import StepSection from './StepSection';

const SESSION_LABEL = (() => {
  const h = new Date().getUTCHours();
  if (h >= 22 || h < 8) return 'SYDNEY';
  if (h >= 8 && h < 9) return 'TOKYO';
  if (h >= 8 && h < 16) return 'LONDON';
  return 'NEW YORK';
})();

function StepObserve() {
  const { price, bid, ask, change, changePct, series, lastUpdated, loading, error } = useGoldPrice();

  const formatTime = (d: Date | null) => {
    if (!d) return '--:--:--';
    return d.toUTCString().slice(17, 25) + ' UTC';
  };

  const isUp = (change ?? 0) >= 0;

  const channels = [
    { label: 'SPREAD', value: '0.18', live: true },
    { label: 'VOLATILITY', value: loading ? '---' : Math.abs(change ?? 0) > 5 ? 'RISING' : 'STABLE', live: true },
    { label: 'SESSION', value: SESSION_LABEL, live: false },
    { label: 'TREND', value: loading ? '---' : isUp ? 'UP' : 'DOWN', live: false },
  ];

  return (
    <StepSection
      id="step-01"
      index="01"
      stage="ingest"
      title="Every Tick Enters The Engine."
      subtitle="Nothing is decided before the data arrives."
      body="The EA is wired directly to the XAUUSD feed and reads it as one continuous stream — price, spread and volatility arriving tick by tick, not a single snapshot. That stream is the only input the engine ever uses."
    >
      <div className="rounded-lg border border-background-300 bg-background-50 overflow-hidden">

        {/* Header bar */}
        <div className="flex items-center justify-between px-4 md:px-5 h-11 border-b border-background-200 bg-background-100">
          <span className="flex items-center gap-2 text-[11px] font-data tracking-widest text-foreground-700">
            <span className="w-1.5 h-1.5 rounded-full bg-profit-500 breathe"></span>
            XAUUSD · LIVE FEED
          </span>
          {error ? (
            <span className="text-[10px] font-data tracking-widest text-loss-500">
              FEED ERROR · RETRYING
            </span>
          ) : loading ? (
            <span className="text-[10px] font-data tracking-widest text-foreground-400 animate-pulse">
              CONNECTING…
            </span>
          ) : (
            <span className="flex items-center gap-1.5 text-[10px] font-data tracking-widest text-profit-600">
              <span className="w-1 h-1 rounded-full bg-profit-500 breathe"></span>
              LIVE · gold-api.com
            </span>
          )}
        </div>

        {/* Price row */}
        <div className="flex items-end justify-between px-4 md:px-5 pt-4 pb-3">
          <div>
            <p className="text-[10px] font-data tracking-[0.22em] text-foreground-400">SPOT PRICE (USD/OZ)</p>
            {loading && !price ? (
              <div className="mt-1.5 h-[34px] w-32 rounded bg-background-200 animate-pulse" />
            ) : (
              <p className="mt-1.5 font-data text-[30px] md:text-[34px] font-semibold leading-none text-foreground-950">
                {price !== null ? price.toFixed(2) : '---'}
              </p>
            )}
          </div>
          <div className="text-right">
            <p className="text-[10px] font-data tracking-[0.22em] text-foreground-400">Δ SESSION</p>
            {loading && !change ? (
              <div className="mt-1.5 h-5 w-20 rounded bg-background-200 animate-pulse ml-auto" />
            ) : (
              <p className={`mt-1.5 font-data text-[15px] font-semibold leading-none ${isUp ? 'text-profit-600' : 'text-loss-500'}`}>
                {change !== null
                  ? `${change >= 0 ? '+' : ''}${change.toFixed(2)} (${changePct !== null ? (changePct >= 0 ? '+' : '') + changePct.toFixed(2) : ''}%)`
                  : '---'}
              </p>
            )}
          </div>
        </div>

        {/* Bid / Ask */}
        <div className="grid grid-cols-2 gap-px bg-background-200 border-y border-background-200">
          <div className="bg-background-50 px-4 py-2.5 flex items-center justify-between">
            <span className="text-[10px] font-data tracking-widest text-foreground-500">BID</span>
            <span className="font-data text-[13px] text-foreground-900">
              {bid !== null ? bid.toFixed(2) : '---'}
            </span>
          </div>
          <div className="bg-background-50 px-4 py-2.5 flex items-center justify-between">
            <span className="text-[10px] font-data tracking-widest text-foreground-500">ASK</span>
            <span className="font-data text-[13px] text-foreground-900">
              {ask !== null ? ask.toFixed(2) : '---'}
            </span>
          </div>
        </div>

        {/* Chart */}
        <div className="relative h-[168px] md:h-[186px] px-1 pt-3">
          <div className="absolute inset-x-4 top-3 flex items-center justify-between pointer-events-none z-10">
            <span className="text-[10px] font-data tracking-widest text-foreground-400">
              PRICE · TICK STREAM
            </span>
            <span className="text-[10px] font-data tracking-widest text-foreground-400">TIME →</span>
          </div>
          {series.length > 0 ? (
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={series} margin={{ top: 30, right: 16, bottom: 10, left: 16 }}>
                <defs>
                  <linearGradient id="ingestFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="oklch(var(--primary-500))" stopOpacity={0.26} />
                    <stop offset="100%" stopColor="oklch(var(--primary-500))" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke="oklch(var(--foreground-950) / 0.06)" vertical={false} />
                <Area
                  type="monotone"
                  dataKey="price"
                  stroke="oklch(var(--primary-500))"
                  strokeWidth={2}
                  fill="url(#ingestFill)"
                  isAnimationActive={false}
                />
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex items-center justify-center h-full">
              <span className="text-[11px] font-data tracking-widest text-foreground-400 animate-pulse">
                FETCHING TICK DATA…
              </span>
            </div>
          )}
        </div>

        {/* Channel tiles */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-px bg-background-200 border-t border-background-200">
          {channels.map((item) => (
            <div key={item.label} className="bg-background-50 px-4 py-3">
              <p className="text-[10px] font-data tracking-widest text-foreground-500">{item.label}</p>
              <p className="mt-1.5 flex items-center gap-1.5 font-heading font-bold text-foreground-950 text-[14px]">
                {item.live && (
                  <span className="w-1.5 h-1.5 rounded-full bg-profit-500 breathe"></span>
                )}
                {item.value}
              </p>
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between border-t border-background-200 bg-background-100 px-4 md:px-5 py-3">
          <span className="flex items-center gap-2 font-data text-[10px] tracking-widest text-foreground-400">
            <i className="ri-arrow-down-line text-[13px] text-profit-600"></i>
            {lastUpdated ? `UPDATED ${formatTime(lastUpdated)}` : 'LAST TICK --:--:--'}
          </span>
          <span className="font-data text-[10px] tracking-widest text-foreground-500">
            {bid !== null && ask !== null
              ? `BID ${bid.toFixed(2)} · ASK ${ask.toFixed(2)}`
              : 'BID --- · ASK ---'}
          </span>
        </div>

        {/* Data source / latency footnote */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 border-t border-background-200 bg-background-200/40 px-4 md:px-5 py-2.5">
          <span className="flex items-start sm:items-center gap-2 text-[10px] font-data tracking-wide text-foreground-500 leading-relaxed">
            <i className="ri-information-line text-[12px] text-foreground-400 mt-px sm:mt-0 shrink-0"></i>
            SOURCE: gold-api.com · SPOT REFERENCE · ~30s REFRESH · MAY BE DELAYED
          </span>
          <span className="flex items-center gap-1.5 text-[10px] font-data tracking-widest text-foreground-600 shrink-0">
            <i className="ri-error-warning-line text-[12px] text-loss-500"></i>
            NOT TRADING GRADE
          </span>
        </div>
      </div>
    </StepSection>
  );
}

export default StepObserve;